import { type FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isArray, isEmpty, isNil } from 'lodash';
// Import Types
import type { VisualizationSubStep, WorkflowVisualization } from 'types';
// Import UI Components
import { Collapse, Panel } from '@geneui/components';
// Import Components
import KeyValueItems, { type KeyValueItemsProps } from 'components/common/KeyValueItems';
// Import Hooks
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
// Import Helpers
import {
    getCJVisualizationArgumentValueText,
    getCJVisualizationDueDateInfo,
    getCJVisualizationReferenceText,
    getCJVisualizationUnlockDate,
    parseCJVisualizationJsonValue,
} from 'services/customerJourney/helpers';
// Import Constants
import {
    l,
    TimeZonePack,
    CJVisualizationDueDateTypeLabels,
    CJVisualizationUnlockConditionTypeLabels,
} from 'constants/common';
// Import Enums
import { CJVisualizationDueDateTypeEnum, CJVisualizationUnlockConditionTypeEnum } from 'enums';
// Import Styles
import styles from './customerJourneyVisualizationStepFeaturesInfo.module.scss';

type InfoItems = KeyValueItemsProps['items'];

interface Props {
    subStep: VisualizationSubStep;
    visualization: WorkflowVisualization;
    blockNames: Record<string, string>;

    referenceProperties: Nullable<AnyObject[]>;
    isReferencePropertiesLoading: boolean;
}

interface StepFeature {
    key: string;
    label: string;
    isEnabled: boolean;
    items: InfoItems;
}

const dueDateValueLabels = {
    [CJVisualizationDueDateTypeEnum.DynamicDate]: l.Date,
    [CJVisualizationDueDateTypeEnum.Duration]: l.Duration,
};

const hasConditionFilters = (condition: unknown): boolean => {
    const model = parseCJVisualizationJsonValue(condition) as Nilable<AnyObject>;
    if (isNil(model) || isEmpty(model)) {
        return false;
    }
    if (model.IsPrimitive === false) {
        return (isArray(model.Conditions) ? model.Conditions : []).some(hasConditionFilters);
    }
    return true;
};

const CustomerJourneyVisualizationStepFeaturesInfo: FC<Props> = (props) => {
    const { subStep, visualization, blockNames, referenceProperties, isReferencePropertiesLoading } = props;

    const { t } = useTranslation();
    const { partnerDateTimeWithoutSecondsFormat } = usePartnerDateFormats();

    const getReferenceText = (value: unknown): Nullable<string> =>
        getCJVisualizationReferenceText(value, blockNames, t);

    const getArgumentValueText = (value: unknown): Nullable<string> =>
        getCJVisualizationArgumentValueText(value, blockNames, t);

    const getConditionText = (condition: unknown): string => t(hasConditionFilters(condition) ? l.Enabled : l.NotSet);

    const getDefaultTemplateItems = (
        templates: Nilable<AnyObject[]>,
        subjectLabel: string,
        withBody: boolean,
    ): InfoItems => {
        const template = templates?.find((item) => isNil(item.Language));
        if (isNil(template)) {
            return [];
        }
        return [
            { label: subjectLabel, value: isEmpty(template.Subject) ? t(l.NA) : template.Subject },
            ...(withBody ? [{ label: l.Description, value: isEmpty(template.Body) ? t(l.NA) : template.Body }] : []),
        ];
    };

    const getUnlockStepText = (): string => {
        const group = visualization.VisualizationSteps.find((item) => item.OrderKey === subStep.Unlock?.GroupOrderKey);
        const step = group?.VisualizationSubSteps.find((item) => item.OrderKey === subStep.Unlock?.StepOrderKey);
        return isNil(group) || isNil(step) ? t(l.NA) : `${group.Name} / ${step.Name}`;
    };

    const getUnlockDateText = (): Nullable<string> => {
        const unlockDate = getCJVisualizationUnlockDate(subStep.Unlock?.Date);
        return isNil(unlockDate)
            ? getArgumentValueText(subStep.Unlock?.Date)
            : `${unlockDate.date.format(partnerDateTimeWithoutSecondsFormat)} ${TimeZonePack[unlockDate.timeZone]}`;
    };

    const features = useMemo((): StepFeature[] => {
        const { Reward, Button: button, Unlock, DueDate, Progress } = subStep;

        const buttonParams = Object.entries(button?.Params ?? {})
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ');
        const unlockType = Unlock?.Type ?? null;
        const dueDate = getCJVisualizationDueDateInfo(
            DueDate,
            blockNames,
            referenceProperties,
            isReferencePropertiesLoading,
            t,
        );

        return [
            {
                key: 'reward',
                label: l.Reward,
                isEnabled: !isNil(Reward),
                items: [
                    ...getDefaultTemplateItems(subStep.VisualizationSubStepRewardTemplates, l.Title, true),
                    { label: l.Condition, value: getConditionText(Reward?.Condition) },
                    {
                        label: l.RewardedBonus,
                        value: t(isNil(getReferenceText(Reward?.ClientBonusId)) ? l.NA : l.Enabled),
                    },
                ],
            },
            {
                key: 'button',
                label: l.Button,
                isEnabled: !isNil(button),
                items: [
                    ...getDefaultTemplateItems(subStep.VisualizationSubStepButtonTemplates, l.ButtonName, false),
                    { label: l.Action, value: button?.Page ?? t(l.NA) },
                    ...(isEmpty(buttonParams) ? [] : [{ label: l.Parameters, value: buttonParams }]),
                ],
            },
            {
                key: 'lock',
                label: l.Lock,
                isEnabled: !isNil(unlockType),
                items: [
                    {
                        label: l.UnlockCondition,
                        value: isNil(unlockType) ? t(l.NA) : t(CJVisualizationUnlockConditionTypeLabels[unlockType]),
                    },
                    ...(unlockType === CJVisualizationUnlockConditionTypeEnum.PassSelectedStep
                        ? [{ label: l.Step, value: getUnlockStepText() }]
                        : []),
                    ...(unlockType === CJVisualizationUnlockConditionTypeEnum.CJCondition
                        ? [{ label: l.Condition, value: getConditionText(Unlock?.Condition) }]
                        : []),
                    ...(unlockType === CJVisualizationUnlockConditionTypeEnum.DateTime
                        ? [{ label: l.DateTime, value: getUnlockDateText() ?? t(l.NA) }]
                        : []),
                ],
            },
            {
                key: 'dueDate',
                label: l.DueDate,
                isEnabled: !isNil(DueDate),
                items: [
                    ...(isNil(dueDate.type) && !dueDate.isTypeLoading
                        ? []
                        : [
                              {
                                  label: l.Type,
                                  value: isNil(dueDate.type) ? null : t(CJVisualizationDueDateTypeLabels[dueDate.type]),
                                  isLoading: dueDate.isTypeLoading,
                              },
                          ]),
                    {
                        label: isNil(dueDate.type) ? l.DueDate : dueDateValueLabels[dueDate.type],
                        value: dueDate.text ?? t(l.NA),
                    },
                ],
            },
            {
                key: 'progress',
                label: l.Progress,
                isEnabled: !isNil(Progress),
                items: [
                    ...getDefaultTemplateItems(subStep.VisualizationSubStepProgressTemplates, l.Title, false),
                    { label: l.Current, value: getArgumentValueText(Progress?.Current) ?? t(l.NA) },
                    { label: l.Goal, value: getArgumentValueText(Progress?.Goal) ?? t(l.NA) },
                ],
            },
        ];
        // DEPS: [getDefaultTemplateItems, getConditionText, getReferenceText, getArgumentValueText, getUnlockStepText,
        //  getUnlockDateText]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        subStep,
        visualization,
        blockNames,
        referenceProperties,
        isReferencePropertiesLoading,
        partnerDateTimeWithoutSecondsFormat,
        t,
    ]);

    const enabledFeatures = features.filter((feature) => feature.isEnabled);

    if (enabledFeatures.length === 0) {
        return null;
    }

    return (
        <div className={styles.stepFeatures}>
            <Collapse allActive={true}>
                {enabledFeatures.map((feature) => (
                    <Panel key={feature.key} title={t(feature.label)}>
                        <div className={styles.stepFeaturesDetails}>
                            <KeyValueItems items={feature.items} vertical={true} />
                        </div>
                    </Panel>
                ))}
            </Collapse>
        </div>
    );
};

CustomerJourneyVisualizationStepFeaturesInfo.displayName = 'CustomerJourneyVisualizationStepFeaturesInfo';

export default memo(CustomerJourneyVisualizationStepFeaturesInfo);
